import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../i18n/LanguageContext';
import { api } from '../../services/api';
import { readFileBody, putToPresignedUrl } from '../../services/uploadService';
import type { GarageDocumentResponse } from '../../types/settings';

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_SIZE = 10 * 1024 * 1024; // 10 MB

export default function OtherDocumentsTab() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [documents, setDocuments] = useState<GarageDocumentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const load = async () => {
    if (!token) return;
    try {
      const data = await api.listMyDocuments(token);
      setDocuments(data.filter((d) => d.documentType === 'OTHER'));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const handleFile = async (file: File) => {
    if (!token) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError(t('settings.invalidFileType'));
      return;
    }
    if (file.size > MAX_SIZE) {
      setError(t('settings.fileSizeExceeded'));
      return;
    }
    setError(null);
    setUploading(true);
    setUploadProgress(0);

    try {
      // Read the file first, so we never mint an upload URL we can't use
      const body = await readFileBody(file);

      const ext = file.name.split('.').pop() || 'bin';
      const { uploadUrl, storageKey } = await api.getGarageUploadUrl(
        { mediaType: 'OTHER_DOCUMENT', fileName: `doc.${ext}`, contentType: file.type },
        token,
      );

      // Transport failures keep the translated generic message this tab always showed
      await putToPresignedUrl(uploadUrl, body, file.type, (p) => setUploadProgress(p.percentage))
        .catch(() => { throw new Error(t('common.uploadFailed')); });

      await api.submitDocument(
        {
          documentType: 'OTHER',
          storageKey,
          originalFilename: file.name,
          contentType: file.type,
          fileSize: file.size,
        },
        token,
      );

      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.uploadFailed'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id: string) => {
    if (!token) return;
    if (!confirm(t('settings.documents.confirmDelete'))) return;
    try {
      await api.deleteMyDocument(id, token);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.failed'));
    }
  };

  if (loading) return <div className="settings-tab-content"><p>{t('common.loading')}</p></div>;

  return (
    <div className="settings-tab-content">
      <div className="settings-section-header">
        <div>
          <h3>{t('settings.documentsTab')}</h3>
          <p className="settings-section-subtitle">{t('settings.documents.subtitle')}</p>
        </div>
        <button
          className="settings-btn-primary"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? `${t('settings.uploading')} ${uploadProgress}%` : t('settings.documents.uploadNew')}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
        />
      </div>

      {error && <div className="settings-toast settings-toast-error">{error}</div>}

      {documents.length === 0 ? (
        <div className="settings-empty-state">
          <p>{t('settings.documents.empty')}</p>
        </div>
      ) : (
        <table className="settings-data-table">
          <thead>
            <tr>
              <th>{t('settings.documents.fileName')}</th>
              <th>{t('settings.documents.uploadedAt')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((d) => (
              <tr key={d.id}>
                <td>{d.originalFilename || '—'}</td>
                <td>{new Date(d.createdAt).toLocaleDateString()}</td>
                <td>
                  {d.downloadUrl && (
                    <a className="settings-view-btn" href={d.downloadUrl} target="_blank" rel="noreferrer">
                      {t('common.view')}
                    </a>
                  )}
                  <button className="settings-link-btn" onClick={() => handleDelete(d.id)}>
                    {t('common.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
